import React from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { createStructuredSelector } from "reselect";
import moment from "moment";
import { withTranslation } from "react-i18next";
import { Typography, Table } from "antd";
import styled from "styled-components";
import ACTIONS from "../store/action";

import { selectPatientTableColumns, selectSite, selectSorting, selectIsFetching } from "../store/selector";
import { formatAssigneesToDisplay } from "../utils/formaters";
import NoOrdersFoundIcon from "../assets/svg/NoOrdersFoundIcon";
import { getSortingChanges } from "../constant/sort";
import { PAGE_SIZE } from "../constant/general";
import { ApiNames } from "../api/api";
import { FilterListForm } from "./SavedList/FilterListForm";
import { PatientNameColumn } from "./customComponent/CommonColumns";
import { TableStyle } from "./SavedList/TableStyle";
import { defaultSort } from "../utils/sort";
import { scrollTableToTop } from "../utils/misc";

const { Text } = Typography;
const tableId = "main-list-view";

const StyledTable = styled(Table)`
  ${TableStyle}
  .ant-table-row {
    cursor: pointer;
  }
  .ant-table {
    margin-left: ${(props) => (props.filtersVisible ? `30px` : `50px`)};
    max-height: ${(props) => (props.isSavedListView ? `calc(100vh - 340px)` : `calc(100vh - 280px)`)};
  }
  .ant-pagination {
    left: ${(props) => (props.filtersVisible ? `30px` : `0px`)};
  }
  .ant-pagination-total-text {
    margin-left: ${(props) => (props.filtersVisible ? `5px` : `50px`)};
  }
  .ant-table-empty {
    max-height: ${() => `calc(100vh - 250px)`};
    .ant-table-tbody {
      height: ${() => `calc(100vh - 310px)`};
    }
  }
`;

function RecentPatientsTableComponent({
  t,
  patients,
  startFetch,
  total,
  onClickTable,
  tableColumns,
  fetchOrders,
  currentPage,
  isFetching,
  isSavedListView,
  filtersVisible
}) {
  const dispatch = useDispatch();
  const setSortingAct = (sorting) => dispatch(ACTIONS.setSorting(sorting));
  const sorting = useSelector(selectSorting);
  const isLoadingRecentPatients = useSelector(selectIsFetching([ApiNames.GetRecentPatients]));
  const setIsLoadingEnabledAct = (isLoadingEnabled) => dispatch(ACTIONS.setIsLoadingEnabled(isLoadingEnabled));
  const setFormPopupsAct = (visible) => dispatch(ACTIONS.setFormPopups(visible));
  const setSearchResultCurrentPageAct = (page) => dispatch(ACTIONS.setSearchResultCurrentPage(page));

  const columns = [
    {
      ...tableColumns.name_id,
      dataIndex: tableColumns.name_id.key,
      key: tableColumns.name_id.key,
      width: 200,
      render: (text, record) => <PatientNameColumn record={record.patient} />,
      ...onClickTable(3)
    },
    {
      ...tableColumns.dob,
      dataIndex: tableColumns.dob.key,
      key: tableColumns.dob.key,
      width: 100,
      ...onClickTable(3)
    },
    {
      ...tableColumns.tmDisplayName,
      dataIndex: tableColumns.tmDisplayName.key,
      key: tableColumns.tmDisplayName.key,
      width: 100,
      ...onClickTable(3)
    },
    {
      ...tableColumns.isActive,
      title: t("header_searchbar.search_results_title.status"),
      dataIndex: tableColumns.isActive.key,
      key: tableColumns.isActive.key,
      width: 100,
      ...onClickTable(3)
    },
    {
      ...tableColumns.viewedAt,
      dataIndex: tableColumns.viewedAt.key,
      key: tableColumns.viewedAt.key,
      width: 100,
      ...onClickTable(3)
    }
  ];

  const onTableChange = (pagination, filters, sorter) => {
    let { current: nextPage } = pagination;

    if (nextPage === currentPage) {
      const { columnIschanged, sortDirectionIschanged, nextColumnName, nextSortBy } = getSortingChanges(
        sorting,
        sorter
      );
      setSortingAct(nextSortBy ? { columnKey: nextColumnName, order: nextSortBy } : defaultSort);
      if ((columnIschanged || sortDirectionIschanged) && currentPage !== 1) {
        nextPage = 1;
      }
    } else {
      setSearchResultCurrentPageAct(nextPage);
    }
    setIsLoadingEnabledAct(true);
    fetchOrders(nextPage, ApiNames.GetRecentPatients, sorter);
    scrollTableToTop(tableId);
  };

  return (
    <div id="list-view">
      <>
        <StyledTable
          id={tableId}
          loading={!startFetch || isFetching || isLoadingRecentPatients}
          onChange={onTableChange}
          columns={columns.filter((col) => tableColumns[col.dataIndex].display)}
          locale={{ emptyText: <NoOrdersFoundIcon /> }}
          isSavedListView={isSavedListView}
          filtersVisible={filtersVisible}
          pagination={{
            style: { margin: 0 },
            size: "small",
            current: currentPage,
            showSizeChanger: false,
            defaultPageSize: PAGE_SIZE,
            pageSize: PAGE_SIZE,
            position: ["bottom"],
            total: total,
            showTotal: (total, range) => (
              <div style={{ display: "flex", flexDirection: "row" }}>
                <Text>
                  {t("savedList.saved_list_orders_page_count", {
                    range_start: range[0],
                    range_end: range[1] ? range[1] : range[0],
                    total
                  })}
                </Text>
              </div>
            )
          }}
          dataSource={patients.map((patient, index) => {
            const { viewedAt, isActive, assignees, rxnormDrug } = patient;
            return {
              ...patient,
              key: index,
              isActive: isActive ? t("header_searchbar.active") : t("header_searchbar.in_active"),
              dob: patient.patient.dob ? moment(patient.patient.dob).format("MM/DD/YYYY") : "",
              viewedAt: viewedAt ? moment.utc(viewedAt).local().format("MM/DD/YYYY h:mm a") : "",
              assigneeNames: formatAssigneesToDisplay(assignees),
              tmDisplayName: rxnormDrug?.tmDisplayName
            };
          })}
        />
      </>
      <FilterListForm setFormPopupsAct={setFormPopupsAct} />
    </div>
  );
}

const mapStateToProps = createStructuredSelector({
  tableColumns: selectPatientTableColumns,
  site: selectSite
});

const RecentPatientsTable = connect(mapStateToProps)(withTranslation()(RecentPatientsTableComponent));

export { RecentPatientsTable };
